import {announce} from '@github-ui/aria-live'
import {copyText} from '@github-ui/copy-to-clipboard'
import {CheckIcon, CopyIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {useEffect, useRef, useState} from 'react'

const copyConfirmationMsDelay = 2000

const HTML_ESCAPES: Readonly<Record<string, string>> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, character => HTML_ESCAPES[character] ?? character)
}

async function copyHtml(html: string, plainText: string): Promise<void> {
  if (typeof ClipboardItem === 'undefined' || typeof navigator.clipboard?.write === 'undefined') {
    return copyText(plainText)
  }

  try {
    await navigator.clipboard.write([
      new ClipboardItem({
        'text/plain': new Blob([plainText], {type: 'text/plain'}),
        'text/html': new Blob([html], {type: 'text/html'}),
      }),
    ])
  } catch {
    await copyText(plainText)
  }
}

export function PullRequestCopyLinkButton({
  owner,
  repository,
  number,
  title,
}: {
  owner: string
  repository: string
  number: number
  title: string
}) {
  const [copied, setCopied] = useState(false)
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => {
    return () => clearTimeout(timeoutIdRef.current)
  }, [])

  const handleCopy = () => {
    const url = new URL(`/${owner}/${repository}/pull/${number}`, window.location.origin).href
    // eslint-disable-next-line github/unescaped-html-literal -- The URL and title are escaped before interpolation.
    const html = `<a href="${escapeHtml(url)}">#${number}</a>: ${escapeHtml(title)}`

    setCopied(true)
    void copyHtml(html, url)
    announce('Copied!')
    clearTimeout(timeoutIdRef.current)
    timeoutIdRef.current = setTimeout(() => setCopied(false), copyConfirmationMsDelay)
  }

  return (
    <IconButton
      aria-label="Copy link"
      className={copied ? 'color-fg-success' : undefined}
      icon={copied ? CheckIcon : CopyIcon}
      onClick={handleCopy}
      size="small"
      variant="invisible"
    />
  )
}
